import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import type {
  GuestCleanupExecuteResult,
  GuestCleanupPreview,
  GuestFieldChange,
  GuestMergePreviewGroup,
} from "@/types/camping";

const FIELD_LABELS: Record<string, string> = {
  title: "Anrede",
  first_name: "Vorname",
  middle_name: "Zweiter Vorname",
  last_name: "Nachname",
  company_name: "Firma",
  vat_id: "USt-IdNr.",
  leitweg_id: "Leitweg-ID",
  date_of_birth: "Geburtsdatum",
  nationality: "Nationalität",
  email: "E-Mail",
  phone: "Telefon",
  street: "Straße",
  house_number: "Hausnummer",
  address_line_2: "Adresszusatz",
  postal_code: "PLZ",
  city: "Ort",
  state_region: "Bundesland",
  country_code: "Land",
};

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") return value;
  return String(value);
}

function FieldChangesTable({ changes }: { changes: GuestFieldChange[] }) {
  if (changes.length === 0) {
    return <p className="text-sm text-label-secondary">Keine Profiländerungen am Ziel-Gast.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-separator">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-separator bg-fill-tertiary/50 text-left">
            <th className="px-3 py-2 font-medium">Feld</th>
            <th className="px-3 py-2 font-medium">Bisher</th>
            <th className="px-3 py-2 font-medium">Neu</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((change) => (
            <tr key={change.field} className="border-b border-separator last:border-0">
              <td className="px-3 py-2">{FIELD_LABELS[change.field] ?? change.field}</td>
              <td className="px-3 py-2 text-label-secondary">{formatValue(change.old_value)}</td>
              <td className="px-3 py-2 font-medium">{formatValue(change.new_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MergeGroupCard({ group }: { group: GuestMergePreviewGroup }) {
  return (
    <article className="space-y-3 rounded-xl border border-separator bg-card-elevated/40 p-4">
      <div>
        <p className="text-sm font-medium text-label">{group.primary_guest_name}</p>
        <p className="text-xs text-label-secondary">
          Bleibt erhalten · {group.match_case_label}
        </p>
      </div>
      <div className="text-sm text-label-secondary">
        <p>
          Wird zusammengelegt mit:{" "}
          <span className="text-label">{group.merged_guest_names.join(", ")}</span>
        </p>
        <p className="mt-1">
          {group.bookings_to_reassign > 0
            ? `${group.bookings_to_reassign} Buchung(en) werden umgehängt. `
            : null}
          {group.invoices_to_reassign > 0
            ? `${group.invoices_to_reassign} Rechnung(en) werden umgehängt. `
            : null}
          {group.contracts_to_reassign > 0
            ? `${group.contracts_to_reassign} Vertrag/Verträge werden umgehängt.`
            : null}
          {group.bookings_to_reassign === 0 &&
          group.invoices_to_reassign === 0 &&
          group.contracts_to_reassign === 0
            ? "Keine verknüpften Datensätze zum Umhängen."
            : null}
        </p>
      </div>
      <FieldChangesTable changes={group.field_changes} />
    </article>
  );
}

export interface GuestCleanupModalProps {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
}

export function GuestCleanupModal({ open, onClose, onCompleted }: GuestCleanupModalProps) {
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<GuestCleanupPreview | null>(null);
  const [result, setResult] = useState<GuestCleanupExecuteResult | null>(null);

  const loadPreview = useCallback(() => {
    setLoading(true);
    setError(null);
    setResult(null);
    api
      .get<GuestCleanupPreview>("/api/v1/guests/cleanup/preview")
      .then(setPreview)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) {
      loadPreview();
    } else {
      setPreview(null);
      setResult(null);
      setError(null);
    }
  }, [open, loadPreview]);

  const handleExecute = () => {
    setExecuting(true);
    setError(null);
    api
      .post<GuestCleanupExecuteResult>("/api/v1/guests/cleanup", {})
      .then((res) => {
        setResult(res);
        onCompleted();
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setExecuting(false));
  };

  const hasMerges = (preview?.total_groups ?? 0) > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Gäste bereinigen"
      description="Doppelte Gäste werden anhand der Wiedererkennungs-Kriterien zusammengelegt. Der aktuellste Datensatz bleibt erhalten."
      size="xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={executing}>
            {result ? "Schließen" : "Abbrechen"}
          </Button>
          {!result && hasMerges ? (
            <Button type="button" variant="warning" loading={executing} onClick={handleExecute}>
              {executing ? "Wird bereinigt…" : "Bereinigung durchführen"}
            </Button>
          ) : null}
          {!result && !hasMerges && !loading ? (
            <Button type="button" variant="secondary" onClick={loadPreview}>
              Erneut prüfen
            </Button>
          ) : null}
        </div>
      }
    >
      {loading ? <p className="text-sm text-label-secondary">Vorschau wird geladen…</p> : null}
      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {result ? (
        <div className="space-y-2 text-sm">
          <p className="font-medium text-label">Bereinigung abgeschlossen</p>
          <p className="text-label-secondary">
            {result.merged_groups} Gruppe(n) zusammengelegt, {result.guests_deleted} Gast/Gäste
            entfernt.
          </p>
          {result.bookings_reassigned > 0 ? (
            <p className="text-label-secondary">
              {result.bookings_reassigned} Buchung(en) umgehängt.
            </p>
          ) : null}
        </div>
      ) : null}

      {!loading && preview && !result ? (
        <div className="space-y-4">
          <p className="text-sm text-label-secondary">
            {preview.guests_checked} Gäste geprüft
            {preview.total_groups > 0
              ? ` · ${preview.total_groups} Zusammenlegung(en), ${preview.guests_to_delete} Datensätze werden entfernt`
              : " · keine Duplikate gefunden"}
          </p>
          {preview.merge_groups.length > 0 ? (
            <div className="space-y-3">
              {preview.merge_groups.map((group) => (
                <MergeGroupCard key={group.primary_guest_id} group={group} />
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-separator bg-fill-tertiary/30 px-4 py-3 text-sm text-label-secondary">
              Es wurden keine zusammenlegbaren Gäste gefunden.
            </p>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
